import React, {Component, ErrorInfo, ReactNode} from 'react';
import {appInsights} from '../../services/appInsights';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error?: Error;
}

/**
 * Error Boundary component that automatically tracks errors to Application Insights
 *
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {hasError: false};
    }

    static getDerivedStateFromError(error: Error): State {
        return {hasError: true, error};
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // Track the error in Application Insights
        appInsights.trackException({
            exception: error,
            properties: {
                componentStack: errorInfo.componentStack,
            },
            severityLevel: 3, // Error level
        });

        // Log error in development
        if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.error('Error caught by ErrorBoundary:', error, errorInfo);
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                this.props.fallback || (
                    <div className="flex items-center justify-center min-h-screen bg-gray-100">
                        <div className="bg-white p-8 rounded-lg shadow-md max-w-md">
                            <h1 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h1>
                            <p className="text-gray-700 mb-4">
                                We&apos;re sorry for the inconvenience. The error has been logged and we&apos;ll look
                                into it.
                            </p>
                            <button
                                onClick={() => window.location.reload()}
                                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
                                Reload Page
                            </button>
                        </div>
                    </div>
                )
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
